import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Bell,
  BookOpen,
  FileText,
  FolderOpen,
  Inbox,
  Network,
  Plus,
  Sparkles,
  Upload,
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { NewCollectionDialog } from "../features/collections/NewCollectionDialog";
import { HomeHero } from "../features/home/HomeHero";

export function HomePage() {
  return (
    <div className="mx-auto max-w-6xl px-6 py-8">
      <HomeHero />

      <div className="home-quickactions mt-6 flex flex-wrap gap-2" data-testid="home-quick-actions">
        <Link
          to="/upload"
          className="inline-flex items-center gap-1.5 rounded-full border border-(--border-subtle) bg-(--bg-canvas) px-3 py-1.5 text-xs hover:bg-(--bg-hover)"
        >
          <Upload size={12} /> Importar
        </Link>
        <NewCollectionDialog
          trigger={
            <button
              type="button"
              data-testid="home-create-collection"
              className="inline-flex items-center gap-1.5 rounded-full border border-(--border-subtle) bg-(--bg-canvas) px-3 py-1.5 text-xs hover:bg-(--bg-hover)"
            >
              <Plus size={12} /> Crear colección
            </button>
          }
        />

        <Link
          to="/notebooks"
          className="inline-flex items-center gap-1.5 rounded-full border border-(--border-subtle) bg-(--bg-canvas) px-3 py-1.5 text-xs hover:bg-(--bg-hover)"
        >
          <BookOpen size={12} /> Nuevo apunte
        </Link>
        <a
          href="/graph"
          className="inline-flex items-center gap-1.5 rounded-full border border-(--border-subtle) bg-(--bg-canvas) px-3 py-1.5 text-xs hover:bg-(--bg-hover)"
        >
          <Network size={12} /> Explorar graph
        </a>
      </div>

      <section className="mt-8" data-testid="continue-section">
        <header className="mb-3 flex items-baseline justify-between">
          <h2 className="text-sm font-semibold">
            Continuar <span className="ml-1 text-(--text-tertiary)">4</span>
          </h2>
          <Link to="/docs" className="text-xs text-(--accent-600) hover:underline">
            Ver todo →
          </Link>
        </header>
        <div className="continue-grid" data-testid="continue-grid">
          <ContinueCard
            kind="Documento"
            icon={<FileText size={11} />}
            title="Despliegue continuo con Cloudflare Workers"
            subtitle="§3 Despliegue · leyendo"
            when="hace 18m"
            to="/docs"
          />
          <ContinueCard
            kind="Colección"
            icon={<FolderOpen size={11} />}
            title="NetBox onboarding Q2"
            subtitle="18 items · actualizada hoy"
            when="hace 1h"
            to="/collections"
          />
          <ContinueCard
            kind="Apunte"
            icon={<BookOpen size={11} />}
            title="Comandos del día a día"
            subtitle="41 entradas"
            when="ayer"
            to="/notebooks"
          />
          <ContinueCard
            kind="Documento"
            icon={<FileText size={11} />}
            title="Postmortem · TLS handshake abril 2026"
            subtitle="§4 Causa raíz"
            when="ayer"
            to="/docs"
          />
        </div>
      </section>

      <section className="mt-8 grid gap-4 md:grid-cols-2">
        <Card title="Actividad reciente" icon={<Bell size={14} />}>
          <ul className="space-y-2 text-sm">
            <ActivityLine
              icon={<FileText size={14} className="text-(--success)" />}
              text="José subió DOCX arquitectura-red.docx → NetBox"
              time="hace 30m"
            />
            <ActivityLine
              icon={<Sparkles size={14} className="text-(--info)" />}
              text='Autolink nuevo: "Cloudflare Access" → /platform/guia'
              time="hace 5m"
            />
            <ActivityLine
              icon={<Bell size={14} className="text-(--warning)" />}
              text='PR #42 abierto: "Actualizar runbook de despliegue"'
              time="hace 1h"
            />
          </ul>
        </Card>
        <Card title="Jobs en cola" icon={<Inbox size={14} />}>
          <ProgressLine label="Convirtiendo propuesta.pdf" progress={70} eta="1m queda" />
          <ProgressLine label='Generando audio "NetBox onboard"' progress={20} eta="3m" />
        </Card>
      </section>

      <div className="mt-6 flex justify-center">
        <Button asChild>
          <Link to="/upload">
            <Upload size={14} /> Importar tu primer doc
          </Link>
        </Button>
      </div>
    </div>
  );
}

function ContinueCard({
  kind,
  icon,
  title,
  subtitle,
  when,
  to,
}: {
  kind: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  when: string;
  to: string;
}) {
  return (
    <Link to={to} className="continue-card no-underline" data-testid="continue-card">
      <div className="continue-kind">
        {icon} {kind}
      </div>
      <div className="continue-title">{title}</div>
      <div className="continue-sub">{subtitle}</div>
      <div className="continue-when">{when}</div>
    </Link>
  );
}

function Card({
  title,
  icon,
  children,
}: {
  title?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-(--border-subtle) bg-(--bg-elevated) p-4 shadow-(--elev-1)">
      {title && (
        <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
          {icon}
          {title}
        </h3>
      )}
      {children}
    </div>
  );
}

function ActivityLine({
  icon,
  text,
  time,
}: {
  icon: React.ReactNode;
  text: string;
  time: string;
}) {
  return (
    <li className="flex items-center gap-2 py-1">
      {icon}
      <span className="flex-1">{text}</span>
      <span className="text-xs text-(--text-tertiary)">{time}</span>
    </li>
  );
}

function ProgressLine({
  label,
  progress,
  eta,
}: {
  label: string;
  progress: number;
  eta: string;
}) {
  return (
    <div className="my-2">
      <div className="flex justify-between text-xs">
        <span>{label}</span>
        <Badge variant="muted">{eta}</Badge>
      </div>
      <div className="mt-1 h-2 overflow-hidden rounded bg-(--bg-canvas)">
        <div className="h-full bg-(--accent-500)" style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
}

// Backwards-compat: previously used <ArrowRight/> icon kept to avoid lint flag.
void ArrowRight;
